import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useSyncExternalStore } from 'react';

import { liftHistoryQueryKey, liftHistoryQueryOptions, toLiftSetSources } from '../api/liftHistoryQueries';
import { toLiftSessions, type LiftSession } from '../model/liftSessions';

/**
 * The histories of these movements that are already in the cache, without reading any. A surface
 * that only NAMES sets (the gallery) takes what the stage and the player have loaded, and a history
 * landing later re-renders it.
 */
export function useLoadedLiftSessions(
  clientId: number,
  exerciseIds: readonly number[],
): ReadonlyMap<number, readonly LiftSession[]> {
  const queryClient = useQueryClient();
  const stamp = useSyncExternalStore(
    (onChange) => queryClient.getQueryCache().subscribe(onChange),
    () =>
      exerciseIds
        .map((id) => String(queryClient.getQueryState(liftHistoryQueryKey(clientId, id))?.dataUpdatedAt ?? 0))
        .join(),
  );
  // The cache is read outside React's sight; `stamp` is the only thing saying it moved.
  return useMemo(() => {
    const updatedAt = stamp.split(',');
    const loaded = new Map<number, readonly LiftSession[]>();
    exerciseIds.forEach((id, index) => {
      if (updatedAt[index] === '0') return;
      const pages = queryClient.getQueryData(liftHistoryQueryOptions(clientId, id).queryKey)?.pages;
      if (pages !== undefined) loaded.set(id, toLiftSessions(toLiftSetSources(pages)));
    });
    return loaded;
  }, [queryClient, clientId, exerciseIds, stamp]);
}
